export const MY_ACCOUNT_SCOPES = [
  'read:me:authentication_methods',
  'create:me:authentication_methods',
  'delete:me:authentication_methods',
].join(' ');

export type AuthenticationMethod = {
  id: string;
  type: string;
  confirmed?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type AuthenticationEnrollment = {
  id: string;
  type: string;
  auth_session: string;
  barcode_uri?: string;
  manual_input_code?: string;
  recovery_code?: string;
};

export function getMyAccountAudience(domain: string): string {
  const normalized = domain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!normalized || !/^[a-z0-9.-]+$/i.test(normalized)) {
    throw new Error('Auth0 domain is unavailable');
  }
  return `https://${normalized}/me/`;
}

function methodsUrl(domain: string, suffix = ''): string {
  return `${getMyAccountAudience(domain)}v1/authentication-methods${suffix}`;
}

async function request<T>(url: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    cache: 'no-store',
    headers: {
      authorization: `Bearer ${token}`,
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(String(body?.message || body?.error || 'Unable to update security settings')) as Error & { status?: number; code?: string };
    error.status = response.status;
    error.code = body?.code || body?.error;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export function listAuthenticationMethods(domain: string, token: string) {
  return request<AuthenticationMethod[]>(methodsUrl(domain), token);
}

export async function startAuthenticationMethod(domain: string, token: string, type: 'totp' | 'recovery-code') {
  const enrollment = await request<Omit<AuthenticationEnrollment, 'type'> & { type?: string }>(methodsUrl(domain), token, {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
  return { ...enrollment, type } as AuthenticationEnrollment;
}

export function verifyAuthenticationMethod(
  domain: string,
  token: string,
  enrollment: AuthenticationEnrollment,
  otpCode?: string,
) {
  const body: Record<string, string> = { auth_session: enrollment.auth_session };
  if (otpCode) body.otp_code = otpCode;
  return request<AuthenticationMethod>(
    methodsUrl(domain, `/${encodeURIComponent(enrollment.id)}/verify`),
    token,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function deleteAuthenticationMethod(domain: string, token: string, methodId: string) {
  return request<void>(methodsUrl(domain, `/${encodeURIComponent(methodId)}`), token, {
    method: 'DELETE',
  });
}
